//Styles
import "./DetailDialog.scss";
// Packages
import React, { useEffect, useRef } from "react";
import { observer } from "mobx-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// Prime React
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
// Local
import { Service } from "../../../models/Service";
import { PanelDialog } from "../../../hooks/UsePanelDialog";
import { ServiceEvent } from "../../../models/ServiceEvent";
import { Participant } from "../../../models/Participant";
import { getAPIUrl } from "../../../utils/Helpers";

type Props = { data: PanelDialog<Service> };

export const DetailDialog: React.FC<Props> = observer((props: Props) => {
    const dialog = props.data;
    const service = dialog.value;
    const lastFocusedElementRef = useRef<HTMLElement | null>(null);

    const getDialogElement = () =>
        document.querySelector(".detail-dialog.p-dialog") as HTMLElement | null;

    // Any time current service changes fetch the participants and service event details
    useEffect(() => {}, [service]);

    useEffect(() => {
        if (!dialog.display) return;

        lastFocusedElementRef.current = document.activeElement as HTMLElement | null;

        const appRoot = document.getElementById("root") as
            | (HTMLElement & { inert?: boolean })
            | null;

        if (appRoot) {
            appRoot.setAttribute("aria-hidden", "true");
            appRoot.inert = true;
        }

        const focusableSelector = [
            "button:not([disabled])",
            "[href]",
            "input:not([disabled])",
            "select:not([disabled])",
            "textarea:not([disabled])",
            "[tabindex]:not([tabindex='-1'])"
        ].join(", ");

        const focusDialog = window.setTimeout(() => {
            const dialogElement = getDialogElement();
            const focusTarget =
                dialogElement?.querySelector(focusableSelector) as HTMLElement | null;

            focusTarget?.focus();
        }, 0);

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== "Tab") return;

            const dialogElement = getDialogElement();
            if (!dialogElement) return;

            const focusableElements = Array.from(
                dialogElement.querySelectorAll(focusableSelector)
            ).filter((element) => {
                const htmlElement = element as HTMLElement;
                return !htmlElement.hasAttribute("disabled") && htmlElement.offsetParent !== null;
            }) as HTMLElement[];

            if (focusableElements.length === 0) {
                event.preventDefault();
                dialogElement.focus();
                return;
            }

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];
            const activeElement = document.activeElement as HTMLElement | null;

            if (event.shiftKey && activeElement === firstElement) {
                event.preventDefault();
                lastElement.focus();
            } else if (!event.shiftKey && activeElement === lastElement) {
                event.preventDefault();
                firstElement.focus();
            }
        };

        const applyLabels = () => {
            document.querySelectorAll(".detail-dialog .p-sortable-column").forEach((el) => {
                const htmlEl = el as HTMLElement;
                const text = htmlEl.textContent?.trim();

                if (text) {
                    const label = `Sort by ${text}`;
                    htmlEl.setAttribute("aria-label", label);
                    htmlEl.setAttribute("title", label);
                }

                htmlEl.setAttribute("tabindex", "0");
                htmlEl.setAttribute("role", "button");

                if (!htmlEl.dataset.keyboardEnabled) {
                    htmlEl.dataset.keyboardEnabled = "true";

                    htmlEl.addEventListener("keydown", (event: KeyboardEvent) => {
                        if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            htmlEl.click();
                        }
                    });
                }
            });

            document.querySelectorAll(".detail-dialog .p-paginator .p-paginator-first").forEach((el) => {
                el.setAttribute("aria-label", "First page");
                el.setAttribute("title", "First page");
            });

            document.querySelectorAll(".detail-dialog .p-paginator .p-paginator-prev").forEach((el) => {
                el.setAttribute("aria-label", "Previous page");
                el.setAttribute("title", "Previous page");
            });

            document.querySelectorAll(".detail-dialog .p-paginator .p-paginator-next").forEach((el) => {
                el.setAttribute("aria-label", "Next page");
                el.setAttribute("title", "Next page");
            });

            document.querySelectorAll(".detail-dialog .p-paginator .p-paginator-last").forEach((el) => {
                el.setAttribute("aria-label", "Last page");
                el.setAttribute("title", "Last page");
            });

            document.querySelectorAll(".detail-dialog .p-paginator .p-paginator-page").forEach((el) => {
                const page = el.textContent?.trim();

                if (page) {
                    const label = `Page ${page}`;
                    el.setAttribute("aria-label", label);
                    el.setAttribute("title", label);
                }
            });

            document.querySelectorAll(".detail-dialog .p-dialog-header-close").forEach((el) => {
                el.setAttribute("aria-label", "Close dialog");
                el.setAttribute("title", "Close dialog");
            });
        };

        applyLabels();

        const mutationObserver = new MutationObserver(() => {
            applyLabels();
        });

        mutationObserver.observe(document.body, {
            childList: true,
            subtree: true
        });

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            window.clearTimeout(focusDialog);
            mutationObserver.disconnect();
            document.removeEventListener("keydown", handleKeyDown);

            if (appRoot) {
                appRoot.removeAttribute("aria-hidden");
                appRoot.inert = false;
            }

            lastFocusedElementRef.current?.focus();
        };
    }, [dialog.display, service]);

    const dateTemplate = (rowData: ServiceEvent) => {
        var displayDate = new Date(rowData.eventDate);
        if (displayDate.getFullYear() === 1) return "N/A";
        return displayDate.toLocaleDateString();
    };

    const timeTemplate = (rowData: ServiceEvent) => {
        var displayTime = new Date(rowData.timeOut);
        if (displayTime.getFullYear() === 1) return "N/A";
        return displayTime.toLocaleTimeString();
    };

    const primaryTemplate = (rowData: Participant) => {
        return (
            <div
                className="p-text-center"
                aria-label={`Is Primary: ${rowData.isPrimary ? "Yes" : "No"}`}
                role="img"
            >
                {rowData.isPrimary && (
                    <FontAwesomeIcon
                        icon={["fad", "check-square"]}
                        className="icon fa-fw"
                        style={
                            {
                                fontSize: "1.5em",
                                "--fa-primary-color": "#FFFFFF",
                                "--fa-secondary-color": "#424242",
                                "--fa-secondary-opacity": 1,
                            } as React.CSSProperties
                        }
                    />
                )}
            </div>
        );
    };

    const dialogHeader = (
        <div className="dialog-header p-d-flex p-jc-between p-ai-center">
            <span className="heading">Details</span>
            <button
                type="button"
                className="custom-close-btn"
                onClick={() => dialog.closeDialog()}
                aria-label="Close dialog"
                title="Close dialog"
            >
                <span className="close-icon" aria-hidden="true">&times;</span>
                <span className="sr-only">Close dialog</span>
            </button>
        </div>
    );

    return (
        <Dialog
            style={{ width: "85vw", maxWidth: "920px" }}
            visible={dialog.display}
            onHide={() => dialog.closeDialog()}
            modal
            blockScroll
            closable={false}
            header={dialogHeader}
            className="detail-dialog"
            appendTo={document.body}
        >
            <div className="p-grid p-fluid p-ml-2">
                <h1 className="p-col-12 p-text-left heading">
                    Case Number:{" "}
                    <span className="case-number">{service.caseNumber}</span>
                </h1>
                <div className="p-col-12 p-text-left">{service.caseCaption}</div>
                <div className="p-col-12 p-text-left p-text-bold">
                    Service: {service.serviceIndex} out of {service.serviceCount}
                </div>

                <div className="p-col-12">
                    <hr />
                </div>

                <h2 className="p-col-12 p-text-left heading">Service Details</h2>

                <div className="p-col-3 p-text-left p-text-bold">File Number:</div>
                <div className="p-col-9 p-text-left">{service.fileNumber}</div>

                <div className="p-col-3 p-text-left p-text-bold">
                    Service Status:
                </div>
                <div className="p-col-9 p-text-left">{service.serviceStatus}</div>

                <div className="p-col-3 p-text-left p-text-bold">
                    Manner of Service:
                </div>
                <div className="p-col-9 p-text-left">{service.manner}</div>

                <div className="p-col-3 p-text-left p-text-bold">Paper Type:</div>
                <div className="p-col-9 p-text-left">{service.requestCategory}</div>

                <div className="p-col-3 p-text-left p-text-bold">Service Type:</div>
                <div className="p-col-9 p-text-left">{service.serviceCategory}</div>

                <div className="p-col-3 p-text-left p-text-bold">
                    Party Served To:
                </div>
                <div className="p-col-9 p-text-left">{service.partyServedTo}</div>

                <div className="p-col-3 p-text-left p-text-bold">
                    Person Served To:
                </div>
                <div className="p-col-9 p-text-left">{service.personServedTo}</div>

                <div className="p-col-12 p-mt-6 p-text-left">
                    <DataTable
                        header={<caption className="table-caption-detail">Participants</caption>}
                        value={service.participants}
                        emptyMessage="No participants found for this service"
                        autoLayout
                        paginator
                        rows={5}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                    >
                        <Column header="Name" sortable field="fullName" />
                        <Column header="Type" sortable field="type" />
                        <Column
                            header="Is Primary"
                            className="p-text-center"
                            sortable
                            field="isPrimary"
                            body={primaryTemplate}
                        />
                    </DataTable>
                </div>

                <div className="p-col-12 p-md-12 p-mt-6 p-text-left">
                    <DataTable
                        header={<caption className="table-caption-detail">Events</caption>}
                        value={service.events}
                        emptyMessage="No Events found for this service"
                        autoLayout
                        paginator
                        rows={5}
                    >
                        <Column header="Type" sortable field="serviceEventCategory" />
                        <Column
                            header="Serviced Date"
                            sortable
                            field="eventDate"
                            body={dateTemplate}
                        />
                        <Column
                            header="Serviced Time"
                            sortable
                            field="timeOut"
                            body={timeTemplate}
                        />
                        <Column header="Deputy" sortable field="deputyName" />
                        <Column header="Badge" sortable field="deputyBadgeNumber" />
                    </DataTable>
                </div>
            </div>
            <div className="p-col-3 p-md-3 p-text-left p-text-bold">
                <a
                    href={`${getAPIUrl()}Reports/ServiceDetail/${service.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="print-link"
                    tabIndex={0}
                >
                    Print
                </a>
            </div>
        </Dialog>
    );
});
